import type {
  LeaderboardEntryPublic,
  RoundEpisodePage,
  RoundPublic,
} from "@softmax/observatory-client/types";
import {
  roundEpisodeViewingHref,
  type RoundEpisodeRow,
} from "../round-episodes";
import type { WatchMatchup, WatchReplay } from "./records";
import { episodeRoster } from "./episode-roster";
import { episodeOutcome } from "./episode-outcome";

type PlayableEpisode = RoundEpisodeRow & {
  episode_id: string;
  coworld_id: string;
  replay_url: string;
};

function isPlayableEpisode(
  episode: RoundEpisodeRow,
): episode is PlayableEpisode {
  if (
    episode.status !== "completed" ||
    !episode.episode_id ||
    !episode.coworld_id ||
    !episode.replay_url
  ) {
    return false;
  }

  return true;
}

export function topTwoReplay({
  episode,
  matchup,
}: {
  episode: RoundEpisodeRow;
  matchup: WatchMatchup;
}): PlayableEpisode | null {
  if (!isPlayableEpisode(episode)) return null;

  const seated = new Set(
    episode.participants.flatMap((participant) =>
      participant.kind === "policy" &&
      !participant.is_filler &&
      !participant.is_seed
        ? [participant.player_id]
        : [],
    ),
  );
  return seated.has(matchup.first.player_id) &&
    seated.has(matchup.second.player_id)
    ? episode
    : null;
}

export function roundTopTwoReplays({
  tag,
  round,
  page,
  matchup,
  leaderboard,
}: {
  tag: string;
  round: RoundPublic;
  page: Pick<RoundEpisodePage, "entries">;
  matchup: WatchMatchup;
  readonly leaderboard: readonly LeaderboardEntryPublic[];
}): WatchReplay[] {
  return page.entries.flatMap((row, index) => {
    const episode = topTwoReplay({ episode: row, matchup });

    if (!episode) return [];

    const episodeNumber = index + 1;
    const { seats, roster } = episodeRoster({
      episode,
      leaderboard,
    });
    return [
      {
        episodeId: episode.episode_id,
        coworldId: episode.coworld_id,
        coworldName: episode.coworld_name ?? null,
        coworldVersion: episode.coworld_version ?? null,
        replayUrl: episode.replay_url,
        finishedAt: episode.completed_at ?? episode.created_at,
        roundNumber: round.round_number,
        episodeNumber,
        code: `${tag}.r${round.round_number}.e${episodeNumber}`,
        matchup,
        seats,
        roster,
        inspectUrl: roundEpisodeViewingHref({
          episode,
          gameAddress: round.division.league.game.game_address,
        }),
        outcome: episodeOutcome({
          episode,
          matchup,
        }),
      },
    ];
  });
}
